import axios from "axios";
import React, { createContext, useCallback, useEffect, useState } from "react";

import { deleteToken, getHeader } from "../../utility/utility";

import { API_ENDPOINT } from "../../constants/constants";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================================================
  // PUBLIC ROUTES
  // ==========================================================================
  //
  // These routes must be accessible without a logged-in user.
  //
  // Exact routes:
  //   /login
  //   /visitor-login
  //   /service-scan
  //   /view-my-badge
  //
  // Dynamic/public routes:
  //   /create-team-account/:id
  //   /activation-link/:code
  //   /mobile-hall-view/:id
  //   /visitor-card-view/:id
  //   /reg-qr/:linkCode
  //
  // ==========================================================================

  const isPublicPath = useCallback((path) => {
    const publicExactPaths = [
      "/activation-link",
      "/login",
      "/visitor-login",
      "/service-scan",
      "/view-my-badge",
    ];

    const publicPathPrefixes = [
      "/activation-link/",
      "/create-team-account/",
      "/mobile-hall-view/",
      "/visitor-card-view/",
      "/reg-qr/",
    ];

    if (publicExactPaths.includes(path)) {
      return true;
    }

    return publicPathPrefixes.some((prefix) => path.startsWith(prefix));
  }, []);

  // ==========================================================================
  // LOGOUT
  // ==========================================================================

  const logout = useCallback(() => {
    // Clear locally stored authentication token.
    deleteToken();

    // Immediately clear user state.
    setUser(null);

    // Redirect to login.
    window.location.href = "/login";
  }, []);

  // ==========================================================================
  // LOAD USER
  // ==========================================================================

  const loadUser = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await axios.get(
        `${API_ENDPOINT}/user/get-profile`,
        getHeader()
      );

      setUser(data);

      // If an authenticated user opens the root route,
      // redirect to their respective dashboard.
      if (window.location.pathname === "/") {
        window.location.href = `/${data.role}`;
      }
    } catch (error) {
      console.error(
        "Failed to load user:",
        error?.response?.status,
        error?.response?.data || error.message
      );

      setUser(null);

      if (error.response && [401, 403].includes(error.response.status)) {
        deleteToken();

        const currentPath = window.location.pathname;

        console.log("Authentication failed on path:", currentPath);

        // Public pages can continue rendering without
        // an authenticated user.
        if (!isPublicPath(currentPath)) {
          console.log("Redirecting unauthenticated user to /login");

          window.location.href = "/login";
        } else {
          console.log("Public route detected. Login redirect skipped.");
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isPublicPath]);

  // ==========================================================================
  // REFRESH USER
  // ==========================================================================
  //
  // Useful after profile updates, settings changes, etc.
  // Unlike loadUser(), this does not toggle the global loading state.
  //
  // ==========================================================================

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_ENDPOINT}/user/get-profile`,
        getHeader()
      );

      setUser(data);

      return data;
    } catch (error) {
      console.error(
        "Failed to refresh user:",
        error?.response?.status,
        error?.response?.data || error.message
      );

      setUser(null);

      if (error.response && [401, 403].includes(error.response.status)) {
        deleteToken();

        const currentPath = window.location.pathname;

        if (!isPublicPath(currentPath)) {
          window.location.href = "/login";
        }
      }

      return null;
    }
  }, [isPublicPath]);

  // ==========================================================================
  // INITIAL LOAD
  // ==========================================================================

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // ==========================================================================
  // CONTEXT
  // ==========================================================================

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        loading,

        loadUser,
        reloadUser: loadUser,
        refreshUser,

        logout,

        isPublicPath,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
